// Playback is local and pure: these helpers never call a live game endpoint.
export function isReplayComplete(game) {
  const phase = typeof game === "string" ? game : game?.currentPhase;
  return ["DOWNLOAD", "DATA_DOWNLOAD", "END", "COMPLETED"].includes(
    String(phase || "").trim().toUpperCase().replace(/[\s-]+/g, "_")
  );
}

export function formatReplayTime(milliseconds) {
  const seconds = Math.max(0, Math.floor((Number(milliseconds) || 0) / 1000));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours ? `${hours}:` : ""}${String(minutes).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

export function getReplayState(bundle, timeMs) {
  if (typeof timeMs !== "number" || !Number.isFinite(timeMs) || timeMs < 0) {
    throw new TypeError("Replay time must be a finite, non-negative number");
  }
  const state = JSON.parse(JSON.stringify(bundle.initialState));
  state.timeMs = Math.min(timeMs, bundle.timeline.durationMs);
  state.appliedEventCount = 0;
  const players = new Map(state.players.map(player => [String(player.id), player]));
  const texts = new Map(state.texts.map(text => [String(text.id), text]));
  for (const event of bundle.events) {
    if (event.timeMs > state.timeMs) break;
    const data = event.data || {};
    const player = players.get(String(event.actorId));
    switch (event.type) {
      case "phase.started":
        state.phase = event.phase;
        state.phaseEnded = false;
        break;
      case "phase.ended": state.phaseEnded = true; break;
      case "chat.message":
        state.messages.push({
          eventId: event.id,
          timeMs: event.timeMs,
          at: event.at,
          playerId: event.actorId,
          groupId: event.groupId,
          phase: event.phase,
          text: data.text,
        });
        break;
      case "player.joined":
        if (player) {
          player.joined = true;
          if (Object.prototype.hasOwnProperty.call(data, "username")) player.username = data.username;
        }
        break;
      case "vote.text":
        if (player) player.textVote = { textId: data.textId, targetResolved: data.targetResolved };
        break;
      case "vote.preliminary":
        if (player) player.preliminaryVotes[String(data.rank)] = { textId: data.textId, targetResolved: data.targetResolved };
        break;
      case "vote.saboteur": if (player) player.saboteurVote = { ...data }; break;
      case "vote.reason": if (player) player.voteReasons[data.stage] = data.reason; break;
      case "text.changed":
        if (texts.has(String(data.textId))) texts.get(String(data.textId)).content = data.content;
        break;
      case "text.title.changed": for (const text of texts.values()) text.title = data.title; break;
      case "host.set":
        if (players.has(String(data.playerId))) players.get(String(data.playerId)).role = "host";
        break;
      case "role.assigned":
        if (players.has(String(data.playerId))) players.get(String(data.playerId)).role = data.role;
        break;
      case "group.created": state.groups.push({ ...data }); break;
      case "group.assigned":
        for (const id of data.playerIds || []) {
          if (players.has(String(id))) players.get(String(id)).groupId = data.groupId;
        }
        break;
    }
    state.appliedEventCount += 1;
  }
  return state;
}

// Number of events applied at a time. Events are sorted by time, so this is a binary search.
export function eventCountAt(bundle, timeMs) {
  let low = 0;
  let high = bundle.events.length;
  while (low < high) {
    const mid = (low + high) >> 1;
    if (bundle.events[mid].timeMs <= timeMs) low = mid + 1;
    else high = mid;
  }
  return low;
}

// Opens the replay in its own browser window. Reusing the window name means a
// second click for the same game focuses the existing window instead of a copy.
export function openReplayWindow(url, gameId) {
  const width = Math.min(window.screen?.availWidth || 1440, 1440);
  const height = Math.min(window.screen?.availHeight || 900, 900);
  const replayWindow = window.open(url, `saboteur-replay-${gameId}`, `width=${width},height=${height}`);
  if (!replayWindow) return false;
  replayWindow.focus();
  return true;
}

export function adjacentReplayTime(bundle, current, direction) {
  if (direction > 0) return bundle.events.find(event => event.timeMs > current)?.timeMs ?? bundle.timeline.durationMs;
  for (let index = bundle.events.length - 1; index >= 0; index -= 1) {
    if (bundle.events[index].timeMs < current) return bundle.events[index].timeMs;
  }
  return 0;
}

export function advanceReplayTime(bundle, current, elapsedMs, speed) {
  return Math.min(bundle.timeline.durationMs, current + Math.max(0, elapsedMs) * speed);
}
